/**
 * Janus lane L1 — 3D movement pricing (M2b level flight · M3a supported elevation · M3b flight ascent/descent ·
 * M3c ground → wade → swim), ONE pricer. plans/janus-lane-3d-movement-pricing-handoff-2026-09-26.md.
 *
 * Everything here is a pure twin of the legacy per-foot walker (move_encounter_creature_logic.php) and the
 * endpoint's path builders; the rollback oracle (tools/probes/l1_traversal_cell_fixture.php capture →
 * tools/test/fixtures/l1-traversal-oracle.json) is the parity reference. The flat walk keeps its own pricer
 * (banked-walk.ts); nothing here is reached by a flat `speed_walking` walk on dry ground.
 *
 * PUBLISHED FOR M3d (burrow) — consume, don't fork:
 *   spatialSamples(polyline)       ft3d_resample_spatial_polyline twin (1 ft 3D arc resample, vertical-corner rule)
 *   flightSamples(origin, dest, r) ft3d_routed_flight_path_positions twin (lifted route, equal ≤1 ft subdivisions)
 *   priceTraversal(samples, …)     per-sample unit cost / multiplier / descent debt, all-or-nothing per foot, AP banking
 *   clipTraversalRoute(poly, s)    the paid 3D prefix (vertices before the stop + the stop), z carried
 * A burrow prices as { speedType: 'speed_burrowing', baseSpeedFt: burrow speed, water: null, multiplierAt: undefined }.
 */
import { ft3dElevationTransitionChainPath, type Ft3dSupportSurface } from '../../encounter-creation-engine/climbing-links.js';
import { ft3dFlightStepCrossesRoof, type FlightRoofBox } from '../../encounter-creation-engine/ft3d-flight-movement.js';

export type Point3 = [number, number, number];
/** One priced foot: its position and the index of the polyline segment (vertex seg-1 → seg) it lies on. */
export interface TraversalSample { x: number; y: number; z: number; seg: number }

const MOVEMENT_EPS = 1e-9; // FT_MOVEMENT_PCT_EPS
const finite = (...values: number[]): boolean => values.every(Number.isFinite);

/** ft3d_resample_spatial_polyline() twin (movement_medium.php), plus each sample's segment index. */
export function spatialSamples(points: ReadonlyArray<readonly number[]>, stepFt = 1): TraversalSample[] {
  const step = Math.max(1e-9, stepFt);
  const clean: Point3[] = [];
  // `seg` indexes the ORIGINAL polyline (clipTraversalRoute slices it): a dropped duplicate vertex must not shift it (Codex L1).
  const original: number[] = [];
  points.forEach((point, index) => {
    if (point.length < 3) return;
    const next: Point3 = [Number(point[0]), Number(point[1]), Number(point[2])];
    if (!finite(...next)) return;
    const last = clean[clean.length - 1];
    if (last && Math.hypot(Math.hypot(next[0] - last[0], next[1] - last[1]), next[2] - last[2]) < 1e-9) return;
    clean.push(next); original.push(index);
  });
  if (clean.length <= 1) return clean.map(p => ({ x: p[0], y: p[1], z: p[2], seg: 0 }));
  const out: TraversalSample[] = [{ x: clean[0]![0], y: clean[0]![1], z: clean[0]![2], seg: 0 }];
  let carry = 0;
  for (let index = 1; index < clean.length; index++) {
    const from = clean[index - 1]!, to = clean[index]!;
    const dx = to[0] - from[0], dy = to[1] - from[1], dz = to[2] - from[2];
    const length = Math.hypot(Math.hypot(dx, dy), dz);
    if (length < 1e-9) continue;
    const previous = clean[index - 2];
    if (previous && (Math.abs(previous[2] - from[2]) > 1e-9) !== (Math.abs(dz) > 1e-9)) {
      const last = out[out.length - 1]!;
      if (Math.hypot(Math.hypot(last.x - from[0], last.y - from[1]), last.z - from[2]) > 1e-9) out.push({ x: from[0], y: from[1], z: from[2], seg: original[index - 1]! });
      carry = 0;
    }
    let travelled = 0;
    while (carry + (length - travelled) >= step - 1e-9) {
      travelled += step - carry;
      carry = 0;
      const ratio = travelled / length;
      out.push({ x: from[0] + dx * ratio, y: from[1] + dy * ratio, z: from[2] + dz * ratio, seg: original[index]! });
    }
    carry += length - travelled;
  }
  const end = clean[clean.length - 1]!;
  if (carry > 1e-9) out.push({ x: end[0], y: end[1], z: end[2], seg: original[clean.length - 1]! });
  else out[out.length - 1] = { x: end[0], y: end[1], z: end[2], seg: out[out.length - 1]!.seg };
  return out;
}

/** The lifted flight polyline — the vertex half of ft3d_routed_flight_path_positions() (pins honoured). */
export function flightVertices(origin: Point3, destination: Point3, planarRoute: ReadonlyArray<readonly number[]> | null): Point3[] {
  const filtered = (planarRoute ?? []).filter(p => p.length >= 2 && Number.isFinite(p[0]) && Number.isFinite(p[1]));
  let route: Array<[number, number, number | null]> = filtered.length >= 2
    ? filtered.map(p => [Number(p[0]), Number(p[1]), p.length > 2 && Number.isFinite(p[2]) ? Number(p[2]) : null])
    : [[origin[0], origin[1], null], [destination[0], destination[1], null]];
  route = route.slice();
  route[0] = [origin[0], origin[1], origin[2]];
  const lastIndex = route.length - 1;
  route[lastIndex] = [destination[0], destination[1], destination[2]];
  const cumulative = [0];
  for (let index = 1; index < route.length; index++) {
    cumulative.push(cumulative[index - 1]! + Math.hypot(route[index]![0] - route[index - 1]![0], route[index]![1] - route[index - 1]![1]));
  }
  const planarLength = cumulative[cumulative.length - 1]!;
  const pinned = route.some((point, index) => index > 0 && index < lastIndex && point[2] !== null);
  if (planarLength < 1e-9 && !pinned) return [[...origin], [...destination]];
  return route.map((point, index): Point3 => {
    if (point[2] !== null) return [point[0], point[1], point[2]];
    let before = index - 1;
    while (before > 0 && route[before]![2] === null) before--;
    let after = index + 1;
    while (after < lastIndex && route[after]![2] === null) after++;
    const span = cumulative[after]! - cumulative[before]!;
    const t = span < 1e-9 ? 0 : (cumulative[index]! - cumulative[before]!) / span;
    const zBefore = route[before]![2]!, zAfter = route[after]![2]!;
    return [point[0], point[1], zBefore + (zAfter - zBefore) * t];
  });
}

/** ft3d_routed_flight_path_positions() twin: every routed vertex kept, each segment split into ceil(len) equal steps. */
export function flightSamples(origin: Point3, destination: Point3, planarRoute: ReadonlyArray<readonly number[]> | null): { vertices: Point3[]; samples: TraversalSample[] } {
  const vertices = flightVertices(origin, destination, planarRoute);
  let distance = 0;
  for (let index = 1; index < vertices.length; index++) {
    const a = vertices[index - 1]!, b = vertices[index]!;
    distance += Math.hypot(Math.hypot(b[0] - a[0], b[1] - a[1]), b[2] - a[2]);
  }
  if (distance < 0.01) return { vertices, samples: [{ x: origin[0], y: origin[1], z: origin[2], seg: 0 }] };
  const samples: TraversalSample[] = [{ x: origin[0], y: origin[1], z: origin[2], seg: 0 }];
  for (let index = 1; index < vertices.length; index++) {
    const a = vertices[index - 1]!, b = vertices[index]!;
    const length = Math.hypot(Math.hypot(b[0] - a[0], b[1] - a[1]), b[2] - a[2]);
    if (length < 1e-9) continue;
    const steps = Math.max(1, Math.ceil(length));
    for (let step = 1; step <= steps; step++) {
      const t = step / steps;
      samples.push({ x: a[0] + (b[0] - a[0]) * t, y: a[1] + (b[1] - a[1]) * t, z: a[2] + (b[2] - a[2]) * t, seg: index });
    }
  }
  const last = samples[samples.length - 1]!;
  samples[samples.length - 1] = { x: destination[0], y: destination[1], z: destination[2], seg: last.seg };
  return { vertices, samples };
}

export type ElevationKind = 'none' | 'step' | 'climb';
export interface ElevationLink { fromZ: number; toZ: number; x: number; y: number; kind: Exclude<ElevationKind, 'none'> }
export interface WalkSupportSurface { id: string; z: number; footprint: Ft3dSupportSurface['footprint'] }

/** ft3d_elevation_transition_kind() twin (the threshold is the size-scaled step, ft_medium_step_up_threshold_ft). */
export function elevationTransitionKind(fromZ: number, toZ: number, thresholdFt: number): ElevationKind {
  const rise = Math.abs(toZ - fromZ);
  if (rise <= 1e-6) return 'none';
  return rise <= thresholdFt + 1e-6 ? 'step' : 'climb';
}

/** The endpoint's ground elevation profile: ft3d_elevation_transition_polyline() with no support footprint, or
 * ft3d_elevation_transition_chain_polyline() over the submitted support chain (the scene's twin, climbing-links.ts). */
export function walkElevationPolyline(
  route: ReadonlyArray<readonly number[]>, fromZ: number, toZ: number,
  surfaces: readonly WalkSupportSurface[], baseSupportZ: number, thresholdFt: number,
): { polyline: Point3[]; links: ElevationLink[] } {
  const planar = route.map(p => [Number(p[0]), Number(p[1])] as [number, number]);
  if (planar.length < 2) return { polyline: [], links: [] };
  if (surfaces.length === 0) {
    const last = planar.length - 1;
    return { polyline: planar.map((p, i): Point3 => [p[0], p[1], i === last ? toZ : fromZ]), links: [] };
  }
  const end = planar[planar.length - 1]!;
  const path = ft3dElevationTransitionChainPath(planar, { x: planar[0]![0], y: planar[0]![1], z: fromZ }, { x: end[0], y: end[1], z: toZ },
    surfaces.map(surface => ({ id: surface.id, z: surface.z, footprint: surface.footprint })), baseSupportZ, thresholdFt);
  return {
    polyline: path.pathFt.map((p): Point3 => [p.x, p.y, p.z]),
    links: (path.links ?? []).map(link => ({ fromZ: link.fromZ, toZ: link.toZ, x: link.boundaryFt.x, y: link.boundaryFt.y, kind: link.kind })),
  };
}

/** Does any step of this flight pass through a closed room's roof (ft3d_flight_step_crosses_roof twin)? */
export function flightCrossesRoof(samples: readonly TraversalSample[], boxes: readonly FlightRoofBox[], bodyHeightFt: number): boolean {
  for (let index = 1; index < samples.length; index++) {
    const a = samples[index - 1]!, b = samples[index]!;
    if (ft3dFlightStepCrossesRoof(boxes, { x: a.x, y: a.y, z: a.z }, { x: b.x, y: b.y, z: b.z }, bodyHeightFt)) return true;
  }
  return false;
}

// ── The derived medium (movement_medium.php) ─────────────────────────────────────────────────────────────────
export interface WaterBody { id: string; x: { min: number; max: number }; y: { min: number; max: number }; surfaceFt: number; depthFt: number }
export interface WaterModel { level: number | null; bodies: WaterBody[] }
type Row = Record<string, unknown>;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

/** ft_shape_water_bodies() twin: malformed bodies are ignored (a bad body must never flood a room). */
export function shapeWaterBodies(shape: unknown): WaterBody[] {
  const raw = record(shape)?.waterBodies;
  if (!Array.isArray(raw)) return [];
  const bodies: WaterBody[] = [];
  for (const value of raw.slice(0, 32)) {
    const body = record(value);
    if (!body || typeof body.id !== 'string' || body.id === '') continue;
    const x = record(body.x), y = record(body.y);
    if (!x || !y || ![x.min, x.max, y.min, y.max].every(isFiniteNumber)) continue;
    if ((x.max as number) <= (x.min as number) || (y.max as number) <= (y.min as number)) continue;
    if (!isFiniteNumber(body.surfaceFt) || !isFiniteNumber(body.depthFt)) continue;
    if (body.depthFt <= 0 || body.depthFt > 500) continue;
    bodies.push({ id: body.id, x: { min: x.min as number, max: x.max as number }, y: { min: y.min as number, max: y.max as number },
      surfaceFt: body.surfaceFt, depthFt: body.depthFt });
  }
  return bodies;
}

/** ft_encounter_water_level() + ft_encounter_water_bodies() twin; null when the encounter has no water at all. */
export function encounterWaterModel(encounter: Row): WaterModel | null {
  const encounters = record(encounter.encounters) ?? {};
  const rawLevel = encounters.encounter_water_level ?? encounter.encounter_water_level;
  const numeric = typeof rawLevel === 'number' || (typeof rawLevel === 'string' && rawLevel.trim() !== '' && Number.isFinite(Number(rawLevel)));
  const level = numeric ? Number(rawLevel) : null;
  let shape = encounters.encounter_shape ?? encounter.encounter_shape;
  if (typeof shape === 'string') {
    if (!shape.includes('"waterBodies"')) shape = null;
    else { try { shape = JSON.parse(shape) as unknown; } catch { shape = null; } }
  }
  const bodies = shapeWaterBodies(shape);
  return level === null && bodies.length === 0 ? null : { level, bodies };
}

/** ft_encounter_submersion_at() twin: feet of water over feet at a place, deepest of plane and bodies. */
export function submersionAt(water: WaterModel, x: number, y: number, feet: number): number {
  let deepest = water.level === null ? 0 : Math.max(0, water.level - feet);
  for (const body of water.bodies) {
    if (x < body.x.min - 1e-6 || x > body.x.max + 1e-6 || y < body.y.min - 1e-6 || y > body.y.max + 1e-6) continue;
    if (feet > body.surfaceFt + 0.05 || feet < body.surfaceFt - body.depthFt - 0.05) continue;
    deepest = Math.max(deepest, body.depthFt);
  }
  return deepest;
}

// ── The pricer ────────────────────────────────────────────────────────────────────────────────────────────────
export const NO_SWIM_MESSAGE = 'You cannot swim — your swimming speed is 0. Something has taken the water from you.';
export const NO_MOVEMENT_TEXT = 'No remaining speed or action points to continue moving!';

/** ft_movement_pay_units() twin — pure dry-run: [pct, apsSpentTotal, ok]. */
export function payUnits(pct: number, apsSpent: number, availableAps: number, units: number, unitCost: number): [number, number, boolean] {
  if (!Number.isFinite(unitCost)) return [pct, apsSpent, units <= 0];
  const whole = Math.floor(units + MOVEMENT_EPS);
  const fraction = Math.max(0, units - whole);
  for (let unit = 0; unit <= whole; unit++) {
    const cost = unit < whole ? unitCost : unitCost * fraction;
    if (cost <= MOVEMENT_EPS) continue;
    if (pct + MOVEMENT_EPS >= cost) pct -= cost;
    else if (apsSpent < availableAps) { apsSpent++; pct += 1; pct -= cost; }
    else return [pct, apsSpent, false];
  }
  if (pct < MOVEMENT_EPS) pct = 0;
  return [pct, apsSpent, true];
}

/** flying_descent_speed_debt_increment() twin (flying_descent_helpers.php). */
export function flyingDescentDebtIncrement(horizontal: number, dz: number): number {
  const adz = Math.abs(dz);
  const theta = horizontal < 1e-12 && adz < 1e-12 ? 0 : adz < 1e-12 ? 90 : Math.atan2(horizontal, adz) * 180 / Math.PI;
  if (theta <= 30) return horizontal;
  if (theta <= 45) { const t = (theta - 30) / 15; return (1 - t) * horizontal + t * 0.5; }
  if (theta <= 75) return 0.5;
  if (theta < 90) return 0.5 + 0.5 * ((theta - 75) / 15);
  return 1;
}

export interface TraversalPriceOptions {
  speedType: string;
  baseSpeedFt: number;
  swimSpeedFt: number;
  movementPct: number;
  availableAps: number;
  /** Terrain ×2 at a sample (the mover positioned there, z included). Absent = clear. */
  multiplierAt?: (sample: TraversalSample) => 1 | 2;
  /** The encounter's water; null = dry (the medium is never consulted). */
  water: WaterModel | null;
  /** max(2.5, the mover's size cube) — wade below it, swim at or above it. */
  wadeThresholdFt: number;
  /** Uses the existing feet position; lifting Z onto the water surface is a separate movement change. */
  walksOnWater?: boolean;
  /** Authored climb faces override the medium's speed, after its denial check. */
  climbStep?: (index: number) => boolean;
  climbSpeedFt?: number;
  /** Hidden halves speed after max(terrain, wade), so it stacks with either. */
  hidden?: boolean;
}
export interface TraversalPrice {
  remainingPct: number;
  speedCostFt: number;
  apsSpent: number;
  complete: boolean;
  /** Samples paid; the stop is samples[stopIndex] (0 = nothing affordable). */
  stopIndex: number;
  /** The legacy warning the loop showed at its halt (out of budget, or a non-swimmer at the water's edge). */
  haltMessage?: string;
  difficult_steps: number;
  wade_steps: number;
  swim_steps: number;
}

/** Shared by the traversal pricer and scene preview: immunity never removes the water medium's cost. */
export function traversalStepCost(sample: Pick<TraversalSample, 'x' | 'y' | 'z'>,
  options: Pick<TraversalPriceOptions, 'water' | 'speedType' | 'walksOnWater' | 'wadeThresholdFt' | 'swimSpeedFt'>,
  startSubmersion: number, terrain: number = 1): { medium: 'ground' | 'wade' | 'swim'; multiplier: number; denied: boolean } {
  let medium: 'ground' | 'wade' | 'swim' = 'ground';
  let denied = false;
  if (options.water && options.speedType !== 'speed_flying' && !options.walksOnWater) {
    const submersion = submersionAt(options.water, sample.x, sample.y, sample.z);
    medium = submersion <= 0 ? 'ground' : submersion >= options.wadeThresholdFt ? 'swim' : 'wade';
    if (medium === 'swim' && !(options.swimSpeedFt > 0)) {
      if (submersion <= startSubmersion + 1e-6) medium = 'wade';
      else denied = true;
    }
  }
  return { medium, multiplier: Math.max(terrain, medium === 'wade' ? 2 : 1), denied };
}

/**
 * The in-combat per-foot loop of move_encounter_creature_logic() over precomputed samples. Each foot: the terrain
 * multiplier at the mover's next position, the derived medium there (a flyer is never in the water; a wade doubles,
 * non-stacking; a swim spends the swimming speed; a non-swimmer HALTS at the water's edge unless the step keeps it
 * as shallow as it started — floundering, priced as a wade), then either the flying-descent debt (dz < 0 in flight)
 * or the 3D step × multiplier, paid all-or-nothing with AP banking. A failed foot halts the walk on the previous one.
 * Out of combat the legacy loop meters nothing; callers skip this entirely.
 */
export function priceTraversal(samples: readonly TraversalSample[], options: TraversalPriceOptions): TraversalPrice | null {
  if (samples.length < 1 || samples.length > 20000 || !Number.isSafeInteger(options.availableAps) || options.availableAps < 0
    || !Number.isFinite(options.movementPct) || options.movementPct < 0 || !(options.baseSpeedFt > 0)
    || samples.some(s => !finite(s.x, s.y, s.z))) return null;
  const flying = options.speedType === 'speed_flying';
  const baseCost = 1 / options.baseSpeedFt;
  const swimCost = options.swimSpeedFt > 0 ? 1 / options.swimSpeedFt : Infinity;
  const climbSpeed = options.climbSpeedFt ?? 0;
  const climbCost = climbSpeed > 0 ? 1 / climbSpeed : Infinity;
  if (!Number.isFinite(baseCost)) return null;
  const water = options.water;
  const start = samples[0]!;
  const startSubmersion = water ? submersionAt(water, start.x, start.y, start.z) : 0;
  let remaining = options.movementPct, apsSpent = 0, cost = 0, debt = 0;
  let difficult = 0, wade = 0, swim = 0;
  for (let i = 1; i < samples.length; i++) {
    const a = samples[i - 1]!, b = samples[i]!;
    const horizontal = Math.hypot(b.x - a.x, b.y - a.y);
    const dz = b.z - a.z;
    const spatial = Math.hypot(horizontal, dz);
    const terrain = options.multiplierAt?.(b) ?? 1;
    const { medium, multiplier: mediumMultiplier, denied } = traversalStepCost(b, options, startSubmersion, terrain);
    if (denied) return { remainingPct: remaining, speedCostFt: cost, apsSpent, complete: false, stopIndex: i - 1, haltMessage: NO_SWIM_MESSAGE,
      difficult_steps: difficult, wade_steps: wade, swim_steps: swim };
    const multiplier = mediumMultiplier * (options.hidden ? 2 : 1);
    const unitCost = options.climbStep?.(i) ? climbCost : medium === 'swim' ? swimCost : baseCost;
    let units: number, nextDebt = debt;
    if (flying && dz < 0) {
      let debtFinal = debt + flyingDescentDebtIncrement(horizontal, dz), payCount = 0;
      while (debtFinal >= 1 - 1e-9) { debtFinal -= 1; payCount++; }
      units = payCount * multiplier;
      nextDebt = debtFinal;
    } else {
      units = spatial * multiplier;
    }
    const [after, spentAfter, ok] = payUnits(remaining, apsSpent, options.availableAps, units, unitCost);
    if (!ok) return { remainingPct: remaining, speedCostFt: cost, apsSpent, complete: false, stopIndex: i - 1, haltMessage: NO_MOVEMENT_TEXT,
      difficult_steps: difficult, wade_steps: wade, swim_steps: swim };
    remaining = after; apsSpent = spentAfter; debt = nextDebt; cost += units;
    if (terrain === 2) difficult++;
    if (medium === 'wade') wade++;
    if (medium === 'swim') swim++;
  }
  return { remainingPct: remaining, speedCostFt: cost, apsSpent, complete: true, stopIndex: samples.length - 1,
    difficult_steps: difficult, wade_steps: wade, swim_steps: swim };
}

/**
 * Can a TRUNCATED walk's stop stand as the loop left it? final_creature_move() re-resolves every non-flight landing
 * (ft_resolve_move_destination) and falls anything unsupported (check_and_apply_falling), so a stop the TS cannot prove
 * settled hands back instead: it must stand on a known level (the floor it started on or a surface top), with its body
 * wholly on the surface it stands on and clear of every solid surface it stands below (oracle S3: a stop overlapping
 * the crate's side was nudged 1 ft back by the resolver).
 */
/** MV11 S6: positions persist at DECIMAL(…,3) — a stop rounded onto a top (11.000 for 11.000111) still stands on it. */
export const TOP_Z_TOLERANCE_FT = 0.0005 + 1e-9;
export function walkStopSettles(stop: { x: number; y: number; z: number }, floorZ: number,
  surfaces: readonly WalkSupportSurface[], radius: number): boolean {
  const onFloor = Math.abs(stop.z - floorZ) <= 1e-6;
  // Standing on a top: the body must be wholly on AT LEAST ONE surface at that height (Codex L1 pass 2 — two disjoint
  // same-height supports must not each demand containment). A standee cylinder (the ellipse inscribed in its bounds) is
  // judged by its inscribed circle.
  const standingOn = (f: WalkSupportSurface['footprint']): boolean => {
    if (f.shape === 'ellipse') {
      const rx = (f.maxX - f.minX) / 2, ry = (f.maxY - f.minY) / 2;
      return Math.hypot(stop.x - (f.minX + rx), stop.y - (f.minY + ry)) + radius <= Math.min(rx, ry) + 1e-6;
    }
    return stop.x - radius >= f.minX - 1e-6 && stop.x + radius <= f.maxX + 1e-6 && stop.y - radius >= f.minY - 1e-6 && stop.y + radius <= f.maxY + 1e-6;
  };
  // Beside a solid it stands below: the body must be clear of its side (a cylinder by its circumscribed circle).
  const clearOf = (f: WalkSupportSurface['footprint']): boolean => {
    if (f.shape === 'ellipse') {
      const rx = (f.maxX - f.minX) / 2, ry = (f.maxY - f.minY) / 2;
      return Math.hypot(stop.x - (f.minX + rx), stop.y - (f.minY + ry)) >= Math.max(rx, ry) + radius - 1e-6;
    }
    const dx = Math.max(f.minX - stop.x, 0, stop.x - f.maxX), dy = Math.max(f.minY - stop.y, 0, stop.y - f.maxY);
    return Math.hypot(dx, dy) >= radius - 1e-6;
  };
  const level = surfaces.filter(s => Math.abs(stop.z - s.z) <= TOP_Z_TOLERANCE_FT);
  if (!onFloor && !level.some(s => standingOn(s.footprint))) return false;
  return surfaces.every(s => !(stop.z < s.z - TOP_Z_TOLERANCE_FT) || clearOf(s.footprint));
}

/** The paid 3D prefix: every polyline vertex before the stop's segment, then the stop itself (z carried). */
export function clipTraversalRoute(polyline: readonly Point3[], stop: TraversalSample, stopPoint: Point3): Point3[] {
  const out: Point3[] = [];
  for (let index = 0; index < Math.max(1, stop.seg) && index < polyline.length; index++) out.push([...polyline[index]!]);
  out.push([...stopPoint]);
  return out;
}
